import { endOfDay, formatDistanceStrict, format as formatter, isBefore, isWithinInterval, startOfDay } from "date-fns";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { ONE_MINUTE_MS } from "@remi/constants";
import { ErrorSeverity, RemiError, RemiPackage } from "@remi/error";

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Parse a bare `YYYY-MM-DD` as that calendar day in the viewer's LOCAL timezone (noon, to dodge DST
 * midnight edges). `new Date("YYYY-MM-DD")` parses as UTC midnight, which renders as the previous day
 * west of UTC — this avoids that off-by-one.
 */
export const parseLocalDate = (isoDate: string): Date => new Date(`${isoDate}T12:00:00`);

/**
 * @description Converts the provided input to the calendar day in Utah and returns a Date set to noon Mountain time.
 * - When given a `Date`, the exact instant is shifted into the America/Denver timezone and snapped to that local day at 12:00.
 *   The `Date` must be an instant. A day-only column stored as UTC midnight (e.g. `SiteInspectorAppointment.scheduledDate`)
 *   is the previous evening in Denver and lands a day early (GAD-960); pass `getDateKeyInTimeZone(day, "UTC")` instead.
 * - When given a `YYYY-MM-DD` string, the function interprets it as a Utah calendar date and sets the time to noon with the correct DST offset.
 *
 * @example
 * parseDateToNoonUtahTime("2025-12-15") // ➜ Date representing 2025-12-15T19:00:00.000Z (noon MST)
 * parseDateToNoonUtahTime(new Date("2025-11-01T03:10:22.000Z")) // ➜ Date representing 2025-10-31T18:00:00.000Z (noon MDT on the local day of the input)
 *
 * @param input - A Date or `YYYY-MM-DD` string to normalize to noon in Utah time.
 * @returns A Date object corresponding to noon Mountain time on the relevant Utah calendar day.
 */
export const parseDateToNoonUtahTime = (input: Date | string): Date => {
  // Build noon from the day string so it takes that day's own UTC offset.
  const dateKey = input instanceof Date ? getDateKeyInTimeZone(input, "America/Denver") : input;

  if (!dateKey.match(/^\d{4}-\d{2}-\d{2}$/)) {
    throw new RemiError({
      remiPackage: RemiPackage.FORMATTING,
      errorType: "INVALID_DATE_STRING",
      severity: ErrorSeverity.LOW,
      data: {
        providedValue: dateKey,
      },
    });
  }

  return dayjs.tz(`${dateKey}T12:00:00`, "America/Denver").toDate();
};

export const setDateToFourAmMountainTime = (input: Date): Date =>
  dayjs.tz(`${dayjs(input).tz("America/Denver").format("YYYY-MM-DD")}T04:00:00`, "America/Denver").toDate();

/**
 * Builds a UTC Date from a calendar date, hour, and IANA timezone.
 * e.g. ("2026-06-13", 16, "America/New_York") ➜ 2026-06-13T20:00:00.000Z (4 PM EDT).
 * dayjs's timezone plugin applies the correct DST offset for the given date.
 */
export const slotDateToUtc = (date: string, hour: number, timezone: string): Date => {
  const hourPadded = String(hour).padStart(2, "0");
  return dayjs.tz(`${date}T${hourPadded}:00:00`, timezone).toDate();
};

export const setDateToNoon = (date: Date): Date => {
  const newDate = new Date(date);
  newDate.setHours(12, 0, 0, 0);
  return newDate;
};

export const setDateToEndOfDay = (date: Date): Date => {
  const newDate = new Date(date);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
};

/** The `YYYY-MM-DD` calendar day an instant falls on in the given IANA zone. */
export const getDateKeyInTimeZone = (instant: Date, timeZone: string): string =>
  dayjs(instant).tz(timeZone).format("YYYY-MM-DD");

export const getTimeKeyInTimeZone = (instant: Date, timeZone: string): string =>
  dayjs(instant).tz(timeZone).format("HH:mm:ss");

export const getUtahTimeDayjs = (date: Date) => {
  return dayjs(date).tz("America/Denver");
};

export const getDateInUtahTime = (date: Date) => {
  return dayjs(date).tz("America/Denver").toDate();
};

// Stepping the day on a timezone-naive `YYYY-MM-DD` applies the TARGET day's offset. Neither
// `.tz("America/Denver").startOf("day").add(n, "day")` nor local-time `Date.setDate` does that — both
// carry the source day's offset across a DST edge and land an hour off, or on the wrong calendar day.
export const getStartOfDayInTimeZone = (input: Date, dayOffset: number, timeZone: string): Date =>
  slotDateToUtc(
    dayjs.utc(getDateKeyInTimeZone(input, timeZone)).add(dayOffset, "day").format("YYYY-MM-DD"),
    0,
    timeZone,
  );

const startOfUtahDayOffsetBy = (input: Date, dayOffset: number): Date =>
  getStartOfDayInTimeZone(input, dayOffset, "America/Denver");

export const startOfUtahDay = (input: Date): Date => startOfUtahDayOffsetBy(input, 0);

export const startOfUtahDayAfter = (input: Date): Date => startOfUtahDayOffsetBy(input, 1);

export const startOfUtahDaysAgo = (input: Date, days: number): Date => startOfUtahDayOffsetBy(input, -days);

export const startOfNextUtahDay = (): Date => startOfUtahDayAfter(new Date());

export const startOfUtahMonthsAgo = (input: Date, monthsAgo: number): Date =>
  slotDateToUtc(
    dayjs.utc(getUtahTimeDayjs(input).format("YYYY-MM-01")).subtract(monthsAgo, "month").format("YYYY-MM-DD"),
    0,
    "America/Denver",
  );

export const startOfNextUtahMonth = (input: Date): Date => startOfUtahMonthsAgo(input, -1);

// A closed month is read as of its last instant, so its commitments and credit are that month's own.
export const getUtahMonthWindow = (month: string | undefined, now: Date): { start: Date; asOf: Date } => {
  const start = month === undefined ? startOfUtahMonthsAgo(now, 0) : slotDateToUtc(`${month}-01`, 0, "America/Denver");
  const end = startOfNextUtahMonth(start);
  return { start, asOf: now < end ? now : new Date(end.getTime() - 1) };
};

const startOfUtcDayOffsetBy = (input: Date, dayOffset: number): Date =>
  dayjs.utc(input).startOf("day").add(dayOffset, "day").toDate();

export const startOfUtcDay = (input: Date): Date => startOfUtcDayOffsetBy(input, 0);

export const startOfUtcDayAfter = (input: Date): Date => startOfUtcDayOffsetBy(input, 1);

export const startOfUtcDaysAgo = (input: Date, days: number): Date => startOfUtcDayOffsetBy(input, -days);

export const getDateForAi = (date: Date): string => {
  const utahTime = dayjs(date).tz("America/Denver");
  return JSON.stringify({
    date: utahTime.format("M/D/YYYY"),
    time: utahTime.format("h:mma"),
  });
};

/**
 * @description given the current item date and the previous item date, return the number of days ago the current item was created
 * @example message sent 1 day ago, today, or a given date
 * @param currItemDate current item date and PrevItemDate
 * @returns formated date string for text messaging
 */
export const daysAgo = (currItemDate: Date, prevItemDate?: Date, alwaysReturnNumDays = false) => {
  const daysPrev = getDaysBeforeToday(currItemDate);
  const prevItemDaysPrev = prevItemDate ? getDaysBeforeToday(prevItemDate) : null;

  if (daysPrev === prevItemDaysPrev) {
    return null;
  }

  if (daysPrev === 0) {
    return "Today";
  } else if (daysPrev === 1) {
    return "Yesterday";
  } else if (alwaysReturnNumDays) {
    return `${daysPrev} days ago`;
  } else {
    return formatDate(currItemDate, { format: DateFormat.MONTH_DAY_YEAR });
  }
};

export const daysUntil = (currItemDate: Date, prevItemDate?: Date, alwaysReturnNumDays = false) => {
  const daysUntil = getDaysBeforeToday(currItemDate);
  const prevItemDaysUntil = prevItemDate ? getDaysBeforeToday(prevItemDate) : null;

  if (daysUntil === prevItemDaysUntil) {
    return null;
  }

  if (daysUntil === 0) {
    return "Today";
  } else if (daysUntil === 1) {
    return "Tomorrow";
  } else if (alwaysReturnNumDays) {
    return `${daysUntil} days until`;
  }
  return formatDate(currItemDate, { format: DateFormat.MONTH_DAY_YEAR });
};

/**
 * @description Given a past date, returns the number of days between that date and today (or a specified current date).
 * @param pastDate - Date to subtract from today or given currentDate.
 * @param currentDate - The date to subtract pastDate from, defaults to today.
 * @param convertToLocalTimeZone - Boolean to first convert dates to local timezone.
 * @returns The number of full days between pastDate and currentDate. If pastDate is after currentDate, returns negative.
 */
export const differenceInDaysCalculator = (
  pastDate: Date,
  currentDate: Date = new Date(),
  convertToLocalTimeZone: boolean = false,
) => {
  if (convertToLocalTimeZone) {
    return dayjs(currentDate).startOf("day").diff(dayjs(pastDate).startOf("day"), "day");
  }
  return Math.ceil(currentDate.getTime() / (1000 * 3600 * 24)) - Math.ceil(pastDate.getTime() / (1000 * 3600 * 24));
};

/**
 * Calculates the difference in days between two dates using UTC date comparison.
 * This ensures that dates are compared based on their UTC calendar date, not local timezone.
 *
 * @param date1 - First date
 * @param date2 - Second date
 * @returns The absolute difference in days (always positive integer)
 */
export const differenceInDaysUTC = (date1: Date, date2: Date): number => {
  const utcDate1 = new Date(date1);
  utcDate1.setUTCHours(0, 0, 0, 0);

  const utcDate2 = new Date(date2);
  utcDate2.setUTCHours(0, 0, 0, 0);

  // Since both dates are set to UTC midnight, the difference is always a whole number of days
  return Math.abs(utcDate2.getTime() - utcDate1.getTime()) / (1000 * 60 * 60 * 24);
};

export function differenceInBusinessDaysCalculator({ startDate, endDate }: { startDate: Date; endDate: Date }): number {
  let start = getUtahTimeDayjs(new Date(startDate)).startOf("day");
  let end = getUtahTimeDayjs(new Date(endDate)).startOf("day");
  const startIsAfterEnd = start > end;
  if (startIsAfterEnd) {
    [start, end] = [end, start];
  }

  let count = 0;
  let current = dayjs(start);

  while (current < end) {
    if (current.day() !== 0 && current.day() !== 6) {
      count++;
    }
    current = current.add(1, "day");
  }

  return startIsAfterEnd ? -count : count;
}

export function differenceInMinutesCalculatorExcludingWeekends({
  startDate,
  endDate = new Date(),
}: {
  startDate: Date;
  endDate?: Date;
}): number {
  let start = getUtahTimeDayjs(new Date(startDate));
  let end = getUtahTimeDayjs(new Date(endDate));

  // Handle negative time differences (when start is after end)
  const startIsAfterEnd = start > end;
  if (startIsAfterEnd) {
    [start, end] = [end, start];
  }

  let totalMinutes = 0;
  let current = getUtahTimeDayjs(start.toDate());

  while (current < end) {
    // Skip weekends for business SLA calculations: 0 = Sunday, 6 = Saturday
    if (current.day() !== 0 && current.day() !== 6) {
      // Define the full day boundaries
      const dayStart = current.startOf("day"); // 00:00:00
      const dayEnd = current.endOf("day"); // 23:59:59

      // Determine actual calculation boundaries for this specific day:
      // - If this is the start day: use actual start time (not 00:00)
      // - If this is the end day: use actual end time (not 23:59)
      // - If this is a middle day: use full day (00:00 to 23:59)
      const actualStart = current.isSame(start, "day") ? current : dayStart;
      const actualEnd = end.isSame(current, "day") ? end : dayEnd;

      if (actualStart < actualEnd) {
        totalMinutes += actualEnd.diff(actualStart, "minute");
      }
    }
    current = getUtahTimeDayjs(current.add(1, "day").startOf("day").toDate());
  }
  // - Positive: SLA already violated (urgent)
  // - Negative: Time remaining until SLA violation (can plan ahead)
  return startIsAfterEnd ? -totalMinutes : totalMinutes;
}

const BUSINESS_START_HOUR = 8;
const BUSINESS_END_HOUR = 18;
const ONE_HOUR_MS = 60 * ONE_MINUTE_MS;

export function getElapsedBusinessHours(from: Date, to: Date): number {
  if (from >= to) {
    return 0;
  }

  const lastDateKey = getDateKeyInTimeZone(to, "America/Denver");
  let dateKey = getDateKeyInTimeZone(from, "America/Denver");
  let totalMs = 0;

  while (dateKey <= lastDateKey) {
    const day = dayjs.utc(dateKey).day();
    if (day !== 0 && day !== 6) {
      const periodStart = Math.max(
        from.getTime(),
        slotDateToUtc(dateKey, BUSINESS_START_HOUR, "America/Denver").getTime(),
      );
      const periodEnd = Math.min(to.getTime(), slotDateToUtc(dateKey, BUSINESS_END_HOUR, "America/Denver").getTime());
      totalMs += Math.max(periodEnd - periodStart, 0);
    }
    dateKey = dayjs.utc(dateKey).add(1, "day").format("YYYY-MM-DD");
  }

  return totalMs / ONE_HOUR_MS;
}

export const getDaysBeforeToday = (date1: Date) => {
  const today = dayjs().startOf("day");
  return today.diff(dayjs(date1).startOf("day"), "day");
};

export const addDaysToDate = (date: Date, days: number) => {
  return dayjs(date).add(days, "day").toDate();
};

export const daysBetween = (
  currItemDate: Date,
  prevItemDate: Date,
  roundingMethod: "floor" | "ceil" | "round" = "floor",
) => {
  const diffInSeconds = formatDistanceStrict(new Date(currItemDate), new Date(prevItemDate), {
    unit: "second",
    roundingMethod,
  });

  const diffInMinutes = formatDistanceStrict(new Date(currItemDate), new Date(prevItemDate), {
    unit: "minute",
    roundingMethod,
  });

  const diffInHours = formatDistanceStrict(new Date(currItemDate), new Date(prevItemDate), {
    unit: "hour",
    roundingMethod,
  });

  const diffInDays = formatDistanceStrict(new Date(currItemDate), new Date(prevItemDate), {
    unit: "day",
    roundingMethod,
  });

  if (parseInt(diffInDays) >= 1) {
    return diffInDays;
  } else if (parseInt(diffInHours) >= 1) {
    return diffInHours;
  } else if (parseInt(diffInMinutes) >= 1) {
    return diffInMinutes;
  } else {
    return diffInSeconds;
  }
};

// Shared constants for date formatting
const ABBREVIATED_DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const FULL_DAYS_OF_WEEK = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;
const ABBREVIATED_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;
const FULL_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

export const DateFormat = {
  DAY: "DAY",
  DAY_MONTH: "DAY_MONTH",
  DATE_MONTH: "DATE_MONTH",
  WEEKDAY_DAY_MONTH_YEAR: "WEEKDAY_DAY_MONTH_YEAR",
  WEEKDAY_MONTH_DAY: "WEEKDAY_MONTH_DAY",
  WEEKDAY_MONTH_DAY_YEAR: "WEEKDAY_MONTH_DAY_YEAR",
  WEEKDAY_DAY_MONTH_YEAR_AT_TIME: "WEEKDAY_DAY_MONTH_YEAR_AT_TIME",
  WEEKDAY_DAY_MONTH_YEAR_TIME: "WEEKDAY_DAY_MONTH_YEAR_TIME",
  WEEKDAY_MONTH_DAY_YEAR_TIME: "WEEKDAY_MONTH_DAY_YEAR_TIME",
  DAY_MONTH_YEAR: "DAY_MONTH_YEAR",
  MONTH_DAY: "MONTH_DAY",
  MONTH_DAY_TIME: "MONTH_DAY_TIME",
  MONTH_DAY_YEAR_TIME: "MONTH_DAY_YEAR_TIME",
  MONTH_DAY_YEAR: "MONTH_DAY_YEAR",
  MONTH_DAY_YEAR_NO_COMMAS: "MONTH_DAY_YEAR_NO_COMMAS",
  TIME_AM_PM: "TIME_AM_PM",
  TIME: "TIME",
  MONTH_DAY_LITERAL_AT_TIME: "MONTH_DAY_LITERAL_AT_TIME",
  YEAR_MONTH_DAY: "YEAR_MONTH_DAY",
} as const;

export type DateFormat = keyof typeof DateFormat;

const DateDefaults: Record<keyof typeof DateFormat, string> = {
  DAY: "--",
  DAY_MONTH: "-- ----",
  DATE_MONTH: "--/--",
  WEEKDAY_DAY_MONTH_YEAR: "-- -- ----",
  WEEKDAY_MONTH_DAY: "-- -- ----",
  WEEKDAY_MONTH_DAY_YEAR: "----, ---- -, ----",
  WEEKDAY_DAY_MONTH_YEAR_AT_TIME: "-- -- ---- @ --:--",
  WEEKDAY_DAY_MONTH_YEAR_TIME: "-- -- ---- --:-- --",
  WEEKDAY_MONTH_DAY_YEAR_TIME: "--- --- --, ----, -:-- --",
  DAY_MONTH_YEAR: "--/--/----",
  MONTH_DAY: "--- --",
  MONTH_DAY_TIME: "-- -- -- --:-- --",
  MONTH_DAY_YEAR_TIME: "-- -- ---- --:-- --",
  MONTH_DAY_YEAR: "------",
  MONTH_DAY_YEAR_NO_COMMAS: "mm dd yyyy",
  TIME_AM_PM: "--:-- --",
  TIME: "--:--",
  MONTH_DAY_LITERAL_AT_TIME: "--- -- at --:-- --",
  YEAR_MONTH_DAY: "YYYY-MM-DD",
};

type FormatDateOptions = {
  format?: keyof typeof DateFormat;
  abbreviateDays?: boolean;
  abbreviateMonths?: boolean;
  dayOptions?: { includeLeadingZero?: boolean };
};

export const formatDate = (date: Date | undefined | null, options?: FormatDateOptions): string => {
  const {
    format = DateFormat.WEEKDAY_DAY_MONTH_YEAR,
    abbreviateDays = true,
    abbreviateMonths = true,
    dayOptions = {
      includeLeadingZero: true,
    },
  } = options ?? {};
  if (date === undefined || date === null) {
    return DateDefaults[format];
  }

  const daysOfWeek = abbreviateDays ? ABBREVIATED_DAYS_OF_WEEK : FULL_DAYS_OF_WEEK;
  const months = abbreviateMonths ? ABBREVIATED_MONTHS : FULL_MONTHS;

  date = new Date(date); // ensure date is the same type of Date object
  const hour = date.getHours();
  const standardHour = hour % 12 || 12;
  const pmOrAm = hour >= 12 ? "PM" : "AM";
  const minute = date.getMinutes();
  const dayOfWeek = daysOfWeek[date.getDay()];
  const dayOfMonth = dayOptions.includeLeadingZero === true ? `0${date.getDate()}`.slice(-2) : date.getDate();
  const month = months[date.getMonth()];
  const monthOfYear = `0${date.getMonth() + 1}`.slice(-2);
  const year = date.getFullYear();

  if (format === DateFormat.WEEKDAY_DAY_MONTH_YEAR) {
    return `${dayOfWeek}, ${dayOfMonth} ${month} ${year}`;
  } else if (format === DateFormat.WEEKDAY_MONTH_DAY_YEAR) {
    return `${dayOfWeek}, ${month} ${dayOfMonth}, ${year}`;
  } else if (format === DateFormat.DAY_MONTH_YEAR) {
    return `${dayOfMonth} ${month} ${year}`;
  } else if (format === DateFormat.WEEKDAY_DAY_MONTH_YEAR_AT_TIME) {
    return `${dayOfMonth} ${month} ${year} @ ${String(standardHour).padStart(2, "0")}:${String(minute).padStart(
      2,
      "0",
    )} ${pmOrAm}`;
  } else if (format === DateFormat.WEEKDAY_DAY_MONTH_YEAR_TIME) {
    return `${dayOfWeek}, ${dayOfMonth} ${month} ${year} ${String(standardHour).padStart(2, "0")}:${String(
      minute,
    ).padStart(2, "0")} ${pmOrAm}`;
  } else if (format === DateFormat.WEEKDAY_MONTH_DAY_YEAR_TIME) {
    return `${dayOfWeek} ${dayOfMonth} ${month}, ${year}, ${String(standardHour)}:${String(minute).padStart(
      2,
      "0",
    )} ${pmOrAm}`;
  } else if (format === DateFormat.MONTH_DAY_TIME) {
    return `${month} ${dayOfMonth}, ${String(standardHour).padStart(2)}:${String(minute).padStart(2, "0")} ${pmOrAm}`;
  } else if (format === DateFormat.MONTH_DAY_YEAR_TIME) {
    return `${month} ${dayOfMonth}, ${year}, ${String(standardHour)}:${String(minute).padStart(2, "0")} ${pmOrAm}`;
  } else if (format === DateFormat.MONTH_DAY_YEAR) {
    return `${month} ${dayOfMonth}, ${year}`;
  } else if (format === DateFormat.TIME_AM_PM) {
    return formatter(date, "h:mm a");
  } else if (format === DateFormat.TIME) {
    return formatter(date, "HH:mm");
  } else if (format === DateFormat.DAY) {
    return `${dayOfMonth}`;
  } else if (format === DateFormat.DAY_MONTH) {
    return `${dayOfMonth} ${month}`;
  } else if (format === DateFormat.DATE_MONTH) {
    return `${monthOfYear}/${dayOfMonth}`;
  } else if (format === DateFormat.MONTH_DAY_YEAR_NO_COMMAS) {
    return `${month} ${dayOfMonth} ${year}`;
  } else if (format === DateFormat.MONTH_DAY_LITERAL_AT_TIME) {
    return `${month} ${dayOfMonth} at ${formatter(date, "h:mma").toLocaleLowerCase()}`;
  } else if (format === DateFormat.YEAR_MONTH_DAY) {
    return `${year}-${monthOfYear}-${dayOfMonth}`;
  } else if (format === DateFormat.WEEKDAY_MONTH_DAY) {
    return `${dayOfWeek}, ${month} ${dayOfMonth}`;
  }
  return `${month} ${dayOfMonth}`;
};

type DateRange =
  | {
      from: Date;
      to: Date;
    }
  | {
      from: Date;
      numberOfDays: number;
    };

export const formatDateRange = (
  range: DateRange,
  options?: FormatDateOptions & {
    separator?: string;
    endDateOptions?: FormatDateOptions;
  },
) => {
  const startDateString = formatDate(range.from, options);
  const endDate = "to" in range ? range.to : addDaysToDate(range.from, range.numberOfDays - 1);
  const endDateString = formatDate(endDate, options?.endDateOptions ?? options);
  return `${startDateString}${options?.separator ?? " - "}${endDateString}`;
};

export const isDateBeforeToday = (date: Date | null | undefined) => {
  if (date === null || date === undefined) {
    return false;
  }

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  return date < todayStart;
};

export const isCompletedBeforeDeadline = ({
  deadline,
  completedDate,
}: {
  deadline: Date;
  completedDate: Date;
}): boolean => {
  return !isBefore(endOfDay(deadline), completedDate);
};

export const isLate = ({
  dueDate,
  completedDate,
}: {
  dueDate: Date | null | undefined;
  completedDate: Date | null | undefined;
}): boolean => {
  if (!dueDate) {
    return false;
  }

  if (!isDateBeforeToday(dueDate)) {
    return false;
  }
  if (!completedDate || isBefore(endOfDay(dueDate), completedDate)) {
    return true;
  }
  return false;
};

/**
 * Time Options
 *
 * @example
 *  <RemiComboBox
 *    size="md"
 *    multiple={false}
 *    className="text-text-tertiary"
 *    data={timeOptions}
 *    selected={timeValue}
 *    setSelected={setTimeValue}
 *  />
 *
 * [{
 *   label: "12:00 AM",
 *   value: "00:00",
 * },
 * {
 *   label: "01:00 AM",
 *   value: "01:00",
 * },
 * ...
 * {
 *   label: "12:00 PM",
 *   value: "12:00",
 * }]
 */
export const timeOptions = Array.from({ length: 24 }, (_, hour) => {
  const pmOrAm = hour >= 12 ? "PM" : "AM";
  const labelHour = ((hour <= 12 ? hour : hour % 12) || 12).toString();
  const valuePaddedHour = hour.toString().padStart(2, "0");
  return { label: `${labelHour}:00 ${pmOrAm}`, value: `${valuePaddedHour}:00` };
});

export const formatScheduledTimeSlot = (slot: string | null | undefined): string | null => {
  if (!slot) {
    return null;
  }
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(slot);
  if (!match) {
    return null;
  }
  const hour = Number(match[1]);
  const minute = match[2];
  if (hour > 23 || Number(minute) > 59) {
    return null;
  }
  const period = hour < 12 ? "AM" : "PM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${minute} ${period}`;
};

export const formatDistanceToNowShort = (
  date: Date,
  opts: { prefix?: string; suffix?: string; allowSeconds?: boolean } = {},
) => {
  if (Number.isNaN(date.getTime())) {
    throw new RemiError({
      remiPackage: RemiPackage.FORMATTING,
      errorType: "INVALID_DATE",
      severity: ErrorSeverity.LOW,
      data: {
        providedValue: date.toString(),
      },
    });
  }

  const seconds = Math.floor(Math.abs(Date.now() - date.getTime()) / 1000);

  const units = [
    { unit: "y", seconds: 60 * 60 * 24 * 365 },
    { unit: "mo", seconds: 60 * 60 * 24 * 30 },
    { unit: "w", seconds: 60 * 60 * 24 * 7 },
    { unit: "d", seconds: 60 * 60 * 24 },
    { unit: "h", seconds: 60 * 60 },
    { unit: "m", seconds: 60 },
    { unit: "s", seconds: 1 },
  ] as const;

  for (const { unit, seconds: unitSeconds } of units) {
    const value = Math.floor(seconds / unitSeconds);
    if (value > 0 && (opts.allowSeconds === true || unit !== "s")) {
      return `${opts.prefix ? `${opts.prefix} ` : ""}${value}${unit}${opts.suffix ? ` ${opts.suffix}` : ""}`;
    }
  }

  return `Just ${opts.prefix ? opts.prefix.toLowerCase() : "now"}`;
};

export const formatDueDateShort = (date: Date) => {
  const msUntilDue = date.getTime() - Date.now();

  if (Math.abs(msUntilDue) < ONE_MINUTE_MS) {
    return "Due now";
  }

  return msUntilDue > 0
    ? formatDistanceToNowShort(date, { prefix: "Due in" })
    : formatDistanceToNowShort(date, { prefix: "Due", suffix: "ago" });
};

export const getEstimatedCompletionDate = (installScheduledAt: Date | null, estimatedProductionDays: number | null) => {
  if (!installScheduledAt || !estimatedProductionDays) {
    return undefined;
  }
  return addDaysToDate(installScheduledAt, estimatedProductionDays - 1);
};

/**
 * Formats a date in UTC timezone with full timestamp precision.
 * Always displays the date in UTC regardless of the user's local timezone.
 *
 * @param date - The date to format
 * @returns Formatted string like "Tue, 01 Jul 2025 23:59:59.999 UTC"
 */
export const formatDateInUTC = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  const dayOfWeek = date.getUTCDay();
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const seconds = date.getUTCSeconds();
  const milliseconds = date.getUTCMilliseconds();

  const formattedTime = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(
    seconds,
  ).padStart(2, "0")}.${String(milliseconds).padStart(3, "0")}`;

  return `${ABBREVIATED_DAYS_OF_WEEK[dayOfWeek]}, ${String(day).padStart(2, "0")} ${
    ABBREVIATED_MONTHS[month]
  } ${year} ${formattedTime} UTC`;
};

// Prevents dates stored as UTC midnight (e.g. BidRequest.lossDate) from rendering a day early
// through formatDate's local-timezone getters.
export const toLocalCalendarDate = (date: Date): Date =>
  new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());

export const isDateWithinRangeInclusive = (date: Date, startDate: Date, endDate: Date) => {
  const start = startOfDay(startDate);
  const end = startOfDay(endDate);
  if (end < start) {
    return false;
  }
  return isWithinInterval(startOfDay(date), { start, end });
};
